import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import styled from 'styled-components';
import { getModalMessage } from '../../shared/constants/modalMessages';
import { getPageMessage } from '../../shared/constants/pageMessages';
import { useExportStationWorkStore } from '../../shared/store/exportStationWorkStore';
import type { SelectedData } from '../../shared/dto/EntityRepository';
import { moveItemsToIndex, selectedDataRowKey } from '../../shared/util/moveItemsToIndex';
import WorkListPdfIndexingRow, {
  WORK_LIST_PDF_INDEX_DRAG_MIME_TYPE,
} from './WorkListPdfIndexingRow';


const AUTO_SCROLL_SPEED = 7;

export type WorkListPdfModalIndexingHandle = {
  getOrderedList: () => SelectedData[];
};

const WorkListPdfModalIndexing = forwardRef<WorkListPdfModalIndexingHandle, object>(
  function WorkListPdfModalIndexing(_props, ref) {
  const { selectedDataList, removeSelectedData } = useExportStationWorkStore();
  const [orderedList, setOrderedList] = useState<SelectedData[]>([]);

  /** 순서 패널에서 블록 드래그에 묶을 행 선택 (IndexWorkViewer 의 reorderPickIds) */
  const [reorderPickKeys, setReorderPickKeys] = useState<string[]>([]);

  const [draggingKeys, setDraggingKeys] = useState<string[]>([]);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const dragMovingKeysRef = useRef<string[]>([]);

  const workListContainerRef = useRef<HTMLDivElement>(null);
  const autoScrollIntervalRef = useRef<number | null>(null);

  useEffect(() => {
    setOrderedList((prev) => {
      const storeKeySet = new Set(selectedDataList.map((row) => selectedDataRowKey(row)));
      const filtered = prev.filter((row) => storeKeySet.has(selectedDataRowKey(row)));
      const filteredKeySet = new Set(filtered.map((row) => selectedDataRowKey(row)));
      const added = selectedDataList.filter(
        (row) => !filteredKeySet.has(selectedDataRowKey(row))
      );
      return [...filtered, ...added];
    });
  }, [selectedDataList]);

  useEffect(() => {
    const valid = new Set(orderedList.map((row) => selectedDataRowKey(row)));
    setReorderPickKeys((prev) => prev.filter((k) => valid.has(k)));
  }, [orderedList]);

  const toggleReorderPick = useCallback((key: string) => {
    setReorderPickKeys((prev) =>
      prev.includes(key) ? prev.filter((x) => x !== key) : [...prev, key]
    );
  }, []);

  const removeFromSelection = useCallback(
    (item: SelectedData) => {
      const key = selectedDataRowKey(item);
      setOrderedList((prev) => prev.filter((row) => selectedDataRowKey(row) !== key));
      removeSelectedData(item);
    },
    [removeSelectedData]
  );
  const stopAutoScroll = () => {
    if (autoScrollIntervalRef.current !== null) {
      cancelAnimationFrame(autoScrollIntervalRef.current);
      autoScrollIntervalRef.current = null;
    }
  };

  const startAutoScroll = (direction: 'up' | 'down') => {
    stopAutoScroll();

    const scroll = () => {
      if (!workListContainerRef.current) return;

      const scrollAmount = direction === 'up' ? -AUTO_SCROLL_SPEED : AUTO_SCROLL_SPEED;
      workListContainerRef.current.scrollTop += scrollAmount;

      autoScrollIntervalRef.current = requestAnimationFrame(scroll);
    };

    autoScrollIntervalRef.current = requestAnimationFrame(scroll);
  };

  const handleContentDragOver = (e: React.DragEvent) => {
    if (dragMovingKeysRef.current.length === 0 || !workListContainerRef.current) return;

    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';

    const container = workListContainerRef.current;
    const rect = container.getBoundingClientRect();
    const mouseY = e.clientY;
    const scrollThreshold = 50;

    const distanceFromTop = mouseY - rect.top;
    const distanceFromBottom = rect.bottom - mouseY;

    if (distanceFromTop < scrollThreshold && container.scrollTop > 0) {
      startAutoScroll('up');
    } else if (
      distanceFromBottom < scrollThreshold &&
      container.scrollTop < container.scrollHeight - container.clientHeight
    ) {
      startAutoScroll('down');
    } else {
      stopAutoScroll();
    }
  };

  const handleContentDragLeave = () => {
    stopAutoScroll();
  };

  useEffect(() => {
    if (dragMovingKeysRef.current.length === 0 && draggingKeys.length === 0) {
      stopAutoScroll();
    }

    return () => {
      stopAutoScroll();
    };
  }, [draggingKeys]);

  const resetAfterDrop = (movedKeys: string[]) => {
    dragMovingKeysRef.current = [];
    setDraggingKeys([]);
    setDragOverIndex(null);
    stopAutoScroll();
    if (movedKeys.length > 1) {
      setReorderPickKeys([]);
    }
  };

  const handleDragRowEnd = () => {
    dragMovingKeysRef.current = [];
    setDraggingKeys([]);
    setDragOverIndex(null);
    stopAutoScroll();
  };

  const beginReorderDrag = useCallback(
    (item: SelectedData) => {
      const lead = selectedDataRowKey(item);
      const orderedSel = orderedList
        .filter((row) => reorderPickKeys.includes(selectedDataRowKey(row)))
        .map((row) => selectedDataRowKey(row));
      const block =
        orderedSel.length > 1 && reorderPickKeys.includes(lead) ? orderedSel : [lead];
      dragMovingKeysRef.current = block;
      setDraggingKeys(block);
    },
    [orderedList, reorderPickKeys]
  );

  const handleDropSpaceDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIndex(index);
  };

  const handleDropSpaceDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = useCallback((e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    e.stopPropagation();

    const fromTransfer = e.dataTransfer.getData(WORK_LIST_PDF_INDEX_DRAG_MIME_TYPE);
    const keys =
      dragMovingKeysRef.current.length > 0
        ? [...dragMovingKeysRef.current]
        : fromTransfer
          ? [String(fromTransfer)]
          : [];

    if (keys.length === 0) return;
    setOrderedList((prev) => moveItemsToIndex(prev, keys, dropIndex));
    resetAfterDrop(keys);
  }, []);

  useImperativeHandle(
    ref,
    () => ({
      getOrderedList: () => orderedList,
    }),
    [orderedList]
  );

  const n = orderedList.length;

  return (
    <WorkListContainer
      ref={workListContainerRef}
      onDragOver={handleContentDragOver}
      onDragLeave={handleContentDragLeave}
    >
      <WorkIndexingDescription>
        <EachDescriptionLine>
          {getPageMessage('WORK_INDEXING_HINT_DRAG_REORDER')}
        </EachDescriptionLine>
      </WorkIndexingDescription>
      {n === 0 ? (
        <NoContentContainer>
          {getModalMessage('WORK_LIST_PDF_INDEXING_PLACEHOLDER')}
        </NoContentContainer>
      ) : (
        <>
          {orderedList.map((item, index) => (
            <React.Fragment key={selectedDataRowKey(item)}>
              <DropSpace
                onDrop={(e) => handleDrop(e, index)}
                onDragOver={(e) => handleDropSpaceDragOver(e, index)}
                onDragLeave={handleDropSpaceDragLeave}
                $isActive={dragOverIndex === index}
              />
              <WorkListPdfIndexingRow
                item={item}
                orderIndex={index}
                isDragging={draggingKeys.includes(selectedDataRowKey(item))}
                isReorderPicked={reorderPickKeys.includes(selectedDataRowKey(item))}
                onToggleReorderPick={() => toggleReorderPick(selectedDataRowKey(item))}
                onRemoveFromSelection={() => removeFromSelection(item)}
                onDragStart={() => beginReorderDrag(item)}
                onDragEnd={handleDragRowEnd}
              />
            </React.Fragment>
          ))}
          <DropSpace
            onDrop={(e) => handleDrop(e, n)}
            onDragOver={(e) => handleDropSpaceDragOver(e, n)}
            onDragLeave={handleDropSpaceDragLeave}
            $isActive={dragOverIndex === n}
            $fillRemaining
          />
        </>
      )}
    </WorkListContainer>
  );
});

/** Select 의 WorkListContainer 와 동일 역할: 작품 목록 열 고정 높이 + 스크롤 */
const WorkListContainer = styled.div`
  width: 100%;
  height: 75vh;
  flex: 3;
  min-height: 0;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  border-top: 1px solid ${({ theme }) => theme.colors.color_Gray_05};
  border-bottom: 1px solid ${({ theme }) => theme.colors.color_Gray_05};
  overflow-y: scroll;
  &::-webkit-scrollbar {
    display: none;
  }
`;

/** Select 의 WorkListContainer 안 NoContentContainer 와 대응 */
const NoContentContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  color: ${({ theme }) => theme.colors.color_Gray_04};
  ${({ theme }) => theme.typography.Body_04};
  white-space: pre-line;
`;

/** IndexWorkViewer 의 WorkIndexingDescription / EachDescriptionLine 과 동일 명명 */
const WorkIndexingDescription = styled.div`
  flex-shrink: 0;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 4px 0;
  border-bottom: 0.5px solid ${({ theme }) => theme.colors.color_Gray_03};
`;

const EachDescriptionLine = styled.div`
  width: 100%;
  height: fit-content;
  display: flex;
  white-space: pre-line;
  ${({ theme }) => theme.typography.Body_04};
  color: ${({ theme }) => theme.colors.color_Gray_04};
`;

const DropSpace = styled.div<{ $isActive?: boolean; $fillRemaining?: boolean }>`
  width: 100%;
  ${({ $fillRemaining }) => ($fillRemaining ? 'flex: 1;' : '')}
  ${({ $fillRemaining, $isActive }) =>
    !$fillRemaining
      ? `
    height: ${$isActive ? '60px' : '8px'};
    min-height: ${$isActive ? '30px' : '8px'};
  `
      : `
    min-height: ${$isActive ? '60px' : '20px'};
  `}
  margin: 2px 0;
  border-radius: 2px;
  background-color: transparent;
  border: ${({ $isActive, theme }) =>
    $isActive ? `1px dashed ${theme.colors.color_Gray_04}` : `0.5px dashed ${theme.colors.color_Gray_06}`};
  transition: all 0.4s ease-out;
`;

export default WorkListPdfModalIndexing;
